import { z } from 'zod'

export const taskVisibilitySchema = z.enum(['ASSIGNED', 'WORKSPACE'])
export type TaskVisibilityScope = z.infer<typeof taskVisibilitySchema>

export const taskActivityActionTypeSchema = z.enum([
  'CREATE',
  'UPDATE',
  'COMPLETE',
  'REOPEN',
  'ADD_NOTE',
  'DELETE',
])
export type TaskActivityActionType = z.infer<typeof taskActivityActionTypeSchema>

export const taskActivitySchema = z.object({
  id: z.string().uuid(),
  actorId: z.string().uuid(),
  actorName: z.string(),
  actionType: taskActivityActionTypeSchema,
  note: z.string().nullable(),
  createdAt: z.string(),
})

export const taskActivityListResponseSchema = z.object({
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  activities: z.array(taskActivitySchema),
})

export const taskSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  creatorUserId: z.string().uuid(),
  creatorName: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  visibilityScope: taskVisibilitySchema,
  dueDate: z.string().nullable(),
  instructions: z.string().nullable().optional(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deleted: z.boolean(),
  assigneeUserIds: z.array(z.string().uuid()),
})

export const taskListResponseSchema = z.object({
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  tasks: z.array(taskSchema),
})

export const createTaskRequestSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  visibilityScope: taskVisibilitySchema,
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  assigneeUserIds: z.array(z.string().uuid()).optional(),
  instructions: z.string().max(2000).optional().nullable(),
})

export const updateTaskRequestSchema = z.object({
  title: z.string().min(1).max(255).optional(),
  description: z.string().max(2000).optional(),
  visibilityScope: taskVisibilitySchema.optional(),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  assigneeUserIds: z.array(z.string().uuid()).optional(),
  instructions: z.string().max(2000).optional().nullable(),
})

export const reassignTaskRequestSchema = z.object({
  assigneeUserIds: z.array(z.string().uuid()),
})

export const completeTaskRequestSchema = z.object({
  note: z.string().max(2000).optional().nullable(),
  nextActivityAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  nextActivityInstructions: z.string().max(500).optional().nullable(),
})

export type Task = z.infer<typeof taskSchema>
export type TaskListResponse = z.infer<typeof taskListResponseSchema>
export type CreateTaskRequest = z.infer<typeof createTaskRequestSchema>
export type UpdateTaskRequest = z.infer<typeof updateTaskRequestSchema>
export type ReassignTaskRequest = z.infer<typeof reassignTaskRequestSchema>
export type CompleteTaskRequest = z.infer<typeof completeTaskRequestSchema>
export type TaskActivity = z.infer<typeof taskActivitySchema>
export type TaskActivityListResponse = z.infer<typeof taskActivityListResponseSchema>

export const feedItemTypeSchema = z.enum(['TASK', 'RECORD_FOLLOWUP'])
export type FeedItemType = z.infer<typeof feedItemTypeSchema>

export const taskFeedItemSchema = z.object({
  type: feedItemTypeSchema,
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  dueDate: z.string().nullable(),
  instructions: z.string().nullable(),
  completed: z.boolean(),
  completedAt: z.string().nullable(),
  completedInstructions: z.string().nullable(),
  assigneeUserIds: z.array(z.string().uuid()),
  recordId: z.string().uuid().nullable(),
  recordGroupId: z.string().uuid().nullable(),
  recordActivitySessionId: z.string().uuid().nullable(),
  followUpTagId: z.string().uuid().nullable(),
  visibilityScope: z.enum(['ASSIGNED', 'WORKSPACE']).nullable(),
})
export type TaskFeedItem = z.infer<typeof taskFeedItemSchema>

export const tasksFeedResponseSchema = z.object({
  items: z.array(taskFeedItemSchema),
  section: z.enum(['remaining', 'completed']),
})
export type TasksFeedResponse = z.infer<typeof tasksFeedResponseSchema>
